import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Session, User } from '@supabase/supabase-js';

interface DeviceToken {
  token: string;
  platform: 'ios' | 'android' | 'web';
  updated_at: string;
}

interface Customer {
  id: string;
  sns_type: string;
  sns_id: string;
  email?: string;
  name?: string;
  nickname: string;
  phone: string;
  gender?: string;
  age_group?: string;
  address?: string;
  carrier?: string;
  deposit_bank?: string;
  deposit_account_number?: string;
  payment_type?: string;
  payment_bank?: string;
  payment_account_number?: string;
  card_company?: string;
  card_number?: string;
  card_expiry_month?: string;
  card_expiry_year?: string;
  fcm_token?: DeviceToken[];
  device_platform?: 'ios' | 'android' | 'web';
  token_updated_at?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  auth_user_id?: string;
}

interface AuthContextType {
  customer: Customer | null;
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (snsId: string) => Promise<void>;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  refreshCustomer: () => Promise<void>;
  updateFcmToken: (fcmToken: string, devicePlatform: 'ios' | 'android' | 'web') => Promise<void>;
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// SNS ID를 기반으로 이메일 생성
const generateEmailFromSnsId = (snsId: string): string => {
  return `${snsId}@bbibbi.local`;
};

// SNS ID를 기반으로 비밀번호 생성 (일관된 해시)
const generatePasswordFromSnsId = (snsId: string): string => {
  // 간단한 해시 함수 (실제 프로덕션에서는 더 강력한 방식 사용)
  let hash = 0;
  for (let i = 0; i < snsId.length; i++) {
    const char = snsId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return `bbibbi_${Math.abs(hash).toString(36)}_${snsId.slice(-8)}`;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Supabase Auth 세션 리스너
  useEffect(() => {
    // 현재 세션 확인
    const initializeAuth = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();

        if (currentSession) {
          setSession(currentSession);
          setUser(currentSession.user);
          console.log('[Auth] 세션 복원됨:', currentSession.user.email);

          // 세션이 있으면 customer 정보 로드
          await loadCustomerFromSession(currentSession);
        } else {
          // 세션 없으면 기존 로컬스토리지 방식으로 폴백
          const savedSnsId = localStorage.getItem('bbibbi_sns_id');
          if (savedSnsId) {
            await loadCustomerBySnsId(savedSnsId);
          }
        }
      } catch (error) {
        console.error('[Auth] 초기화 오류:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Auth 상태 변경 리스너 (non-blocking)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log('[Auth] 상태 변경:', event, newSession?.user?.email);

        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (event === 'SIGNED_OUT') {
          setCustomer(null);
          localStorage.removeItem('bbibbi_sns_id');
        } else if (newSession && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
          // non-blocking으로 customer 로드 (signInWithPassword 리턴을 막지 않도록)
          loadCustomerFromSession(newSession).catch(err => {
            console.error('[Auth] Customer 로드 실패:', err);
          });
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 세션에서 customer 로드
  const loadCustomerFromSession = async (currentSession: Session) => {
    try {
      // 이메일에서 sns_id 추출 (카카오/네이버 등 커스텀 인증)
      const email = currentSession.user.email;
      if (email && email.endsWith('@bbibbi.local')) {
        const snsId = email.replace('@bbibbi.local', '');
        await loadCustomerBySnsId(snsId);
        return;
      }

      // auth_user_id로 조회 (구글 등 OAuth)
      const { data: customerByAuthId } = await supabase
        .from('customers')
        .select('*')
        .eq('auth_user_id', currentSession.user.id)
        .maybeSingle();

      if (customerByAuthId) {
        setCustomer(customerByAuthId);
        localStorage.setItem('bbibbi_sns_id', customerByAuthId.sns_id);
        console.log('[Auth] Customer 로드 성공 (auth_user_id):', customerByAuthId.nickname);
        return;
      }

      // localStorage에서 sns_id로 폴백
      const savedSnsId = localStorage.getItem('bbibbi_sns_id');
      if (savedSnsId) {
        await loadCustomerBySnsId(savedSnsId);
        return;
      }

      console.log('[Auth] Customer를 찾을 수 없음');
    } catch (error) {
      console.error('[Auth] Customer 로드 오류:', error);
    }
  };

  // SNS ID로 customer 로드
  const loadCustomerBySnsId = async (snsId: string) => {
    try {
      // 대소문자 구분 없이 조회 (Supabase Auth가 이메일을 소문자로 변환하므로)
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .ilike('sns_id', snsId)
        .maybeSingle();

      if (error) {
        console.error('[Auth] Customer 조회 실패:', error);
        localStorage.removeItem('bbibbi_sns_id');
        return;
      }

      if (data) {
        setCustomer(data);
        localStorage.setItem('bbibbi_sns_id', snsId);
        console.log('[Auth] Customer 로드 성공:', data.nickname);
      } else {
        console.log('[Auth] Customer 없음 (신규 회원)');
      }
    } catch (error) {
      console.error('[Auth] Customer 로드 오류:', error);
    }
  };

  const login = async (snsId: string) => {
    try {
      const email = generateEmailFromSnsId(snsId);
      const password = generatePasswordFromSnsId(snsId);

      console.log('[Auth] Supabase Auth 로그인 시도:', email);

      // 먼저 로그인 시도
      console.log('[Auth] signInWithPassword 호출 중...');
      let { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      console.log('[Auth] signInWithPassword 완료:', signInError ? '에러' : '성공', signInError?.message);

      // 로그인 실패시 회원가입 시도
      if (signInError && signInError.message.includes('Invalid login credentials')) {
        console.log('[Auth] 계정 없음, 회원가입 시도');

        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
        });

        if (signUpError) {
          console.error('[Auth] 회원가입 실패:', signUpError);
          return;
        }

        if (signUpData?.session) {
          signInData = { session: signUpData.session, user: signUpData.user! };
        }
        console.log('[Auth] 회원가입 성공');
      } else if (signInError) {
        console.error('[Auth] 로그인 오류:', signInError);
        return;
      }

      if (signInData?.session) {
        setSession(signInData.session);
        setUser(signInData.user);
        console.log('[Auth] 세션 생성 성공');

        // Customer 테이블에 auth_user_id 업데이트 (비동기, 실패해도 무시)
        supabase
          .from('customers')
          .update({ auth_user_id: signInData.user?.id })
          .ilike('sns_id', snsId)
          .then(({ error }) => {
            if (error) {
              console.warn('[Auth] auth_user_id 업데이트 실패 (무시):', error.message);
            } else {
              console.log('[Auth] auth_user_id 업데이트 성공');
            }
          });
      }

      localStorage.setItem('bbibbi_sns_id', snsId);
      console.log('[Auth] 로그인 완료');
    } catch (error) {
      console.error('[Auth] 로그인 오류:', error);
    }
  };

  const logout = async () => {
    console.log('[Auth] 로그아웃 시작');

    // 1. Supabase signOut 먼저 호출
    try {
      await supabase.auth.signOut();
      console.log('[Auth] Supabase signOut 성공');
    } catch (error) {
      console.error('[Auth] Supabase signOut 실패:', error);
    }

    // 2. React 상태 정리
    setCustomer(null);
    setSession(null);
    setUser(null);

    // 3. 모든 스토리지 클리어
    localStorage.clear();
    sessionStorage.clear();

    // 4. 쿠키 클리어
    document.cookie.split(";").forEach((c) => {
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    // 5. IndexedDB 클리어 (Supabase가 사용할 수 있음)
    if (window.indexedDB) {
      const databases = await window.indexedDB.databases?.();
      if (databases) {
        databases.forEach((db) => {
          if (db.name) {
            window.indexedDB.deleteDatabase(db.name);
            console.log('[Auth] IndexedDB 삭제:', db.name);
          }
        });
      }
    }

    console.log('[Auth] 로그아웃 완료 - 모든 스토리지 클리어됨');
  };

  // 회원탈퇴
  const deleteAccount = async () => {
    if (!customer) return;

    try {
      console.log('[Auth] 회원탈퇴 시작:', customer.id);

      // 1. 개인정보 삭제 + is_active = false + deleted_at 기록
      const { error } = await supabase
        .from('customers')
        .update({
          is_active: false,
          deleted_at: new Date().toISOString(),
          name: null,
          phone: null,
          email: null,
          address: null,
          nickname: '탈퇴회원',
          carrier: null,
          gender: null,
          age_group: null,
          deposit_bank: null,
          deposit_account_number: null,
          payment_type: null,
          payment_bank: null,
          payment_account_number: null,
          card_company: null,
          card_number: null,
          card_expiry_month: null,
          card_expiry_year: null,
          fcm_token: null,
          device_platform: null,
          token_updated_at: null,
          sns_id: `deleted_${customer.id}_${Date.now()}`,
          auth_user_id: null,
        })
        .eq('id', customer.id);

      if (error) {
        console.error('[Auth] 회원탈퇴 DB 업데이트 실패:', error);
        throw error;
      }

      console.log('[Auth] 회원탈퇴 DB 처리 완료');

      // 2. 로그아웃 처리
      await logout();

      console.log('[Auth] 회원탈퇴 완료');
    } catch (error) {
      console.error('[Auth] 회원탈퇴 오류:', error);
      throw error;
    }
  };

  // 액세스 토큰 가져오기
  const getAccessToken = (): string | null => {
    return session?.access_token ?? null;
  };

  const refreshCustomer = async () => {
    if (customer) {
      await login(customer.sns_id);
    }
  };

  const updateFcmToken = async (fcmToken: string, devicePlatform: 'ios' | 'android' | 'web') => {
    if (!customer) {
      console.log('⚠️ customer가 없어서 FCM 토큰 업데이트 스킵');
      return;
    }

    console.log('💾 FCM 토큰 DB 저장 시작:', {
      customer_id: customer.id,
      platform: devicePlatform,
      token: fcmToken.substring(0, 20) + '...'
    });

    try {
      // 현재 저장된 토큰들 가져오기
      const { data: currentData } = await supabase
        .from('customers')
        .select('fcm_token')
        .eq('id', customer.id)
        .single();

      let tokens: DeviceToken[] = [];

      // 기존 토큰 배열 파싱
      if (currentData && Array.isArray(currentData.fcm_token)) {
        tokens = currentData.fcm_token;
      }

      // 같은 플랫폼의 기존 토큰 제거 (중복 방지)
      tokens = tokens.filter(t => t.platform !== devicePlatform);

      // 새 토큰 추가
      tokens.push({
        token: fcmToken,
        platform: devicePlatform,
        updated_at: new Date().toISOString()
      });

      // 최대 5개까지만 유지 (오래된 것부터 제거)
      if (tokens.length > 5) {
        tokens.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
        tokens = tokens.slice(0, 5);
      }

      const { data, error } = await supabase
        .from('customers')
        .update({
          fcm_token: tokens,
          device_platform: devicePlatform,
          token_updated_at: new Date().toISOString()
        })
        .eq('id', customer.id)
        .select()
        .single();

      if (error) {
        console.error('❌ FCM 토큰 업데이트 실패:', error);
        return;
      }

      if (data) {
        console.log('✅ FCM 토큰 DB 저장 성공!', tokens.length + '개 토큰');
        console.log('📋 저장된 토큰 목록:', tokens.map(t => ({
          platform: t.platform,
          token: t.token.substring(0, 30) + '...',
          updated_at: t.updated_at
        })));
        setCustomer(data);
      }
    } catch (error) {
      console.error('❌ FCM 토큰 업데이트 오류:', error);
    }
  };

  const isAuthenticated = !!session || !!customer;

  return (
    <AuthContext.Provider value={{
      customer,
      session,
      user,
      isLoading,
      isAuthenticated,
      login,
      logout,
      deleteAccount,
      refreshCustomer,
      updateFcmToken,
      getAccessToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
